import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Trophy, TrendingUp, TrendingDown, Package, ChevronDown, ChevronUp, Calendar, DollarSign, Percent, FileDown } from "lucide-react";

const API = `${window.location.origin}/api`;

function formatBRL(v) {
  if (v == null || isNaN(v)) return "R$ 0,00";
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatMesLabel(iso) {
  if (!iso) return "";
  const [y, m] = iso.split("-");
  const nomes = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  return `${nomes[parseInt(m, 10) - 1]}/${y}`;
}

function opcoesMeses() {
  // Últimos 12 meses (atual + 11 anteriores)
  const hoje = new Date();
  const opts = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({ value: iso, label: formatMesLabel(iso) });
  }
  return opts;
}

export default function RankingProdutosPage() {
  const [mes, setMes] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aberto, setAberto] = useState({}); // produto_id → boolean

  const carregar = async (mesQuery) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/produtos/ranking`, { params: { mes: mesQuery } });
      setDados(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar ranking");
      setDados(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar(mes);
  }, [mes]);

  const produtos = dados?.produtos || [];
  const opts = opcoesMeses();

  return (
    <div className="space-y-6" data-testid="ranking-produtos-page">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-2" style={{ fontFamily: "Work Sans" }}>
            <Trophy className="h-7 w-7 text-amber-500" /> Ranking do mês
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Produtos mais vendidos, com quantidade por rota, receita, custo e margem.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            className="gap-2 border-red-300 text-red-700 hover:bg-red-50"
            onClick={() => {
              const token = localStorage.getItem("token");
              const url = `${API}/produtos/ranking/pdf?mes=${mes}`;
              // Abre em nova aba com token no header via fetch → blob
              fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.blob())
                .then(b => {
                  const u = URL.createObjectURL(b);
                  window.open(u, "_blank");
                  setTimeout(() => URL.revokeObjectURL(u), 60000);
                })
                .catch(() => toast.error("Erro ao gerar PDF"));
            }}
            data-testid="btn-ranking-pdf"
          >
            <FileDown className="h-4 w-4" /> Baixar PDF
          </Button>
          <Calendar className="h-4 w-4 text-gray-500" />
          <select
            value={mes}
            onChange={(e) => setMes(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
            data-testid="ranking-mes-select"
          >
            {opts.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* KPIs mensais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Notas emitidas" valor={dados?.total_qtd_notas ?? 0} raw icon={Package} cor="blue" />
        <KPI label="Receita total" valor={formatBRL(dados?.total_receita || 0)} icon={DollarSign} cor="emerald" />
        <KPI label="Custo total (CMV)" valor={formatBRL(dados?.total_custo || 0)} icon={TrendingDown} cor="amber" />
        <KPI
          label="Margem bruta"
          valor={formatBRL(dados?.total_margem || 0)}
          sub={
            dados?.total_receita > 0
              ? `${((dados.total_margem / dados.total_receita) * 100).toFixed(1)}%`
              : "—"
          }
          icon={TrendingUp}
          cor="purple"
        />
      </div>

      {/* Tabela */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Trophy className="h-5 w-5 text-amber-500" /> Vencedores de {formatMesLabel(dados?.mes || mes)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-gray-500">Carregando ranking…</p>
          ) : produtos.length === 0 ? (
            <p className="text-center py-8 text-gray-500" data-testid="ranking-vazio">
              Nenhuma venda registrada em {formatMesLabel(dados?.mes || mes)}.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-gray-500">
                    <th className="py-2 w-10">#</th>
                    <th className="py-2">Produto</th>
                    <th className="py-2 text-right">Qtd</th>
                    <th className="py-2 text-right">Receita</th>
                    <th className="py-2 text-right hidden md:table-cell">Custo</th>
                    <th className="py-2 text-right">Margem</th>
                    <th className="py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {produtos.map((p, idx) => {
                    const isOpen = !!aberto[p.produto_id];
                    const pos = idx + 1;
                    const medalha = pos === 1 ? "🥇" : pos === 2 ? "🥈" : pos === 3 ? "🥉" : "";
                    const margemCor =
                      p.margem_percent >= 50 ? "text-emerald-700" :
                      p.margem_percent >= 30 ? "text-blue-700" :
                      p.margem_percent >= 15 ? "text-amber-700" :
                      "text-red-700";
                    return (
                      <>
                        <tr
                          key={p.produto_id}
                          className="border-b hover:bg-slate-50 cursor-pointer"
                          onClick={() => setAberto((s) => ({ ...s, [p.produto_id]: !isOpen }))}
                          data-testid={`ranking-row-${p.produto_id}`}
                        >
                          <td className="py-2.5 font-bold text-gray-500">{medalha || pos}</td>
                          <td className="py-2.5">
                            <div className="font-medium">{p.nome}</div>
                            <div className="text-[11px] text-gray-500">{p.codigo_sku}{p.categoria ? ` · ${p.categoria}` : ""}</div>
                          </td>
                          <td className="py-2.5 text-right font-semibold">{p.quantidade_total}</td>
                          <td className="py-2.5 text-right font-semibold text-emerald-700">{formatBRL(p.receita_total)}</td>
                          <td className="py-2.5 text-right hidden md:table-cell text-gray-600">{formatBRL(p.custo_total)}</td>
                          <td className={`py-2.5 text-right font-bold ${margemCor}`}>
                            {formatBRL(p.margem_valor)}
                            <div className="text-[10px] font-normal opacity-70 inline-flex items-center gap-0.5 ml-1">
                              <Percent className="h-2.5 w-2.5" />
                              {p.margem_percent.toFixed(1)}
                            </div>
                          </td>
                          <td className="py-2.5 text-center text-gray-400">
                            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-slate-50/70">
                            <td colSpan={7} className="px-3 py-3">
                              <div className="mb-2 text-xs font-bold uppercase text-gray-500 tracking-wider">
                                Quantidade por rota
                              </div>
                              {p.por_rota && p.por_rota.length > 0 ? (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                  {p.por_rota.map((r) => (
                                    <div key={r.rota_id || "sem-rota"} className="rounded-lg border bg-white px-3 py-2">
                                      <div className="text-xs text-gray-500">{r.rota_nome}</div>
                                      <div className="flex items-baseline justify-between mt-0.5">
                                        <span className="text-lg font-bold">{r.quantidade}</span>
                                        <span className="text-xs text-emerald-700 font-semibold">{formatBRL(r.receita)}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400">Sem rotas registradas.</p>
                              )}
                              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                <MiniStat label="Preço médio venda" val={formatBRL(p.quantidade_total > 0 ? p.receita_total / p.quantidade_total : 0)} />
                                <MiniStat label="Preço custo (cadastro)" val={formatBRL(p.preco_custo)} />
                                <MiniStat label="Preço venda (cadastro)" val={formatBRL(p.preco_venda_cadastro)} />
                                <MiniStat label="Margem %" val={`${p.margem_percent.toFixed(1)}%`} highlight={margemCor} />
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KPI({ label, valor, sub, icon: Icon, cor = "blue", raw = false }) {
  const cores = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
  }[cor] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <div className={`rounded-xl border p-3 ${cores}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase font-bold tracking-wider opacity-80">{label}</div>
        {Icon && <Icon className="h-4 w-4 opacity-70" />}
      </div>
      <div className="mt-1.5 text-xl font-extrabold">{raw ? valor : valor}</div>
      {sub && <div className="text-[11px] mt-0.5 opacity-70">{sub}</div>}
    </div>
  );
}

function MiniStat({ label, val, highlight }) {
  return (
    <div className="rounded border bg-white px-2.5 py-1.5">
      <div className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">{label}</div>
      <div className={`text-sm font-bold mt-0.5 ${highlight || "text-gray-800"}`}>{val}</div>
    </div>
  );
}
